import { Dispatch, useCallback } from 'react';
import {
    API,
    ApplicationType,
    CHANGE_TYPE,
    endpoints,
    EventDataProps,
    IApplication,
    ISepEventDetails,
    IShoppingWindowAllEvents,
    IShoppingWindowEvents,
    setError,
    startSpinner,
    stopSpinner,
    setApplicationProperty,
    useAppDispatch,
    useModal,
    YesNo,
} from '../utils';
import { API_STATUS } from '@constants/common.constants';

export default () => {
    let isSEPDatesPresent = false;
    let sepStartDate = '';
    let sepEndDate = '';
    let covStartDate = '';
    const isFinancial = YesNo.Y;
    let applicationType: string;
    let isEventPresent = false;
    let sepEventDetails: IShoppingWindowAllEvents;

    const { openModal, closeModal } = useModal();
    const encHouseHoldId = sessionStorage.getItem('encHouseHoldId');
    const dispatch: Dispatch<any> = useAppDispatch();
    const actions = { setError, startSpinner, stopSpinner };

    const getOpenShoppingWindowDetails = useCallback(async (application: IApplication) => {
        const caseNumber: string = application.caseNumber;

        if (application.appType === ApplicationType.OpenEnrollment) {
            applicationType = ApplicationType.OE;
        } else if (application.isQep) {
            applicationType = ApplicationType.QEP;
        } else {
            applicationType = ApplicationType.SEP;
        }

        try {
            const resp = await API.getData(
                endpoints.MEMBER_APP_LIST_GET_SEP_PERIOD_DATES.processReplacements({ caseNumber }),
                dispatch,
                actions,
                true,
            );

            if (resp) {
                sepStartDate = resp?.sepStartDate;
                sepEndDate = resp?.sepEndDate;
                covStartDate = resp?.coverageStartDate;
                isSEPDatesPresent = true;
            }
            await getOpenWindowApplicantEvents(caseNumber);
        } catch (error) {
            throw new Error('Error fetching SEP period dates: ' + error);
        }
    }, []);

    const getOpenWindowApplicantEvents = async (caseNumber: string) => {
        if (isSEPDatesPresent) {
            try {
                const resp = await API.getData(
                    endpoints.MEMBER_APP_LIST_SEP_EVENT_DETAILS.processReplacements({ caseNumber, encHouseHoldId }),
                    dispatch,
                    actions,
                    true,
                );

                if (resp?.status === '200' && resp?.applicants?.length > 0) {
                    sepEventDetails = resp;
                    sepEventDetails.sepStartDate = sepStartDate;
                    sepEventDetails.sepEndDate = sepEndDate;
                    sepEventDetails.coverageStartDate = covStartDate;

                    sepEventDetails.applicants = sepEventDetails.applicants.filter(
                        item => item.eligibilityStatus === ApplicationType.QHP,
                    );

                    isEventPresent = true;
                    const promises = sepEventDetails.applicants.flatMap((applicant, i) =>
                        applicant.events.map((event, j) => {
                            event.previousEventId = event.eventId;
                            event.previousEventDate = event.eventDate;
                            event.previousEventNameLabel = event.eventLabel;
                            event.eventDate = '';
                            event.eventLabel = '';

                            const data = {
                                isFinancial,
                                displayOnUI: YesNo.Y,
                                changeType: CHANGE_TYPE,
                                isAddOverride: YesNo.N,
                            };
                            return getAllEvents(data, i, j);
                        }),
                    );

                    Promise.all(promises).then(() => {
                        return getQHPApplicantEvents(isEventPresent, caseNumber);
                    });
                } else if (resp?.status === '200') {
                    await getQHPApplicantEvents(isEventPresent, caseNumber);
                }
            } catch (error) {
                openModal('notifyModal', {
                    headerId: 'cap.applications.modal.openshoppingwindow2',
                    bodyContentId: 'cap.applications.modal.csrsubmissionfailtitle',
                    bodyMsgId: 'cap.applications.modal.srsubmissionfailcontent',
                    bodyMsgId2: 'cap.applications.modal.csrsubmissionfailcontent2',
                    buttonId: 'button.ok',
                });
            }
        } else {
            await getQHPApplicantEvents(isEventPresent, caseNumber);
        }
    };

    const getAllEvents = async (data: EventDataProps, applicantIndex: number, eventIndex: number) => {
        return new Promise((resolve, reject) => {
            const params = [
                endpoints.MEMBER_APP_LIST_GET_SEP_EVENTS_BY_APPLICATION_TYPE,
                data,
                'POST',
                true,
                undefined,
                'application/json;charset=utf-8',
            ];

            (async () => {
                try {
                    const resp = await API.postData(params, dispatch, actions);
                    const events =
                        resp?.sepEventses?.map(item => ({
                            eventNameLabel: item.label,
                            eventId: item._links.self.href.split('/').pop(),
                            type: item.type,
                            allowFuture: item.allowFutureEvent,
                        })) || [];

                    sepEventDetails.applicants[applicantIndex].events[eventIndex].eventList = events;
                    sepEventDetails.applicants[applicantIndex].events[eventIndex].eventList.sort((a, b) =>
                        a.eventNameLabel.localeCompare(b.eventNameLabel),
                    );
                    resolve(events);
                } catch (error) {
                    reject(error);
                }
            })();
        });
    };

    const getQHPApplicantEvents = async (isEventPresent: boolean, caseNumber: string) => {
        if (!sepEventDetails && !isEventPresent) {
            try {
                const resp = await API.getData(
                    endpoints.MEMBER_APP_LIST_GET_QHP_APPLICANTS.processReplacements({ caseNumber }),
                    dispatch,
                    actions,
                    true,
                );

                if (resp?.status === '200') {
                    sepEventDetails = resp;

                    const data = {
                        isFinancial,
                        displayOnUI: YesNo.Y,
                        changeType: CHANGE_TYPE,
                        isAddOverride: YesNo.N,
                    };

                    const promises = sepEventDetails.applicants.map((applicant, i) => {
                        applicant.events = [{} as IShoppingWindowEvents];
                        return getAllEvents(data, i, 0);
                    });

                    await Promise.all(promises);
                    openShoppingWindowModal(caseNumber);
                }
            } catch (error) {
                throw new Error('Error fetching QHP applicant events: ' + error);
            }
        } else {
            openShoppingWindowModal(caseNumber);
        }
    };

    const openShoppingWindowModal = (caseNumber: string) => {
        if (sepEventDetails) {
            sepEventDetails.caseNumber = caseNumber;
            sepEventDetails.isFinancial = isFinancial === YesNo.Y;
            sepEventDetails.applicationType = applicationType;
            sepEventDetails.overrideCommentText = '';
            sepEventDetails.sepStartDate = '';
            sepEventDetails.sepEndDate = '';
            sepEventDetails.coverageStartDate = '';

            sepEventDetails.applicants.forEach(applicant => {
                applicant.events.forEach(event => {
                    event.eventDate = '';
                    event.eventLabel = '';
                });
            });

            openModal('shoppingWindowModal', { sepEventDetails, isEventPresent });
        }
    };

    const getPlanWindowApplicantEvents = (sepEventDetails: IShoppingWindowAllEvents, isEventPresent: boolean) => {
        return sepEventDetails.applicants.flatMap(applicant => {
            return applicant.events.map(event => {
                const eventNameLabel =
                    event.eventList.find(item => item.eventId === event.eventId)?.eventNameLabel || '';

                return {
                    firstName: applicant.firstName,
                    middleName: applicant.middleName,
                    lastName: applicant.lastName,
                    applicantId: applicant.applicantId,
                    eventId: event.eventId,
                    eventNameLabel: eventNameLabel,
                    ...(isEventPresent && {
                        applicantEventId: event.applicantEventId,
                        previousEventId: event.previousEventId,
                        previousEventNameLabel: event.previousEventNameLabel,
                    }),
                };
            });
        });
    };

    const saveShoppingWindowOverride = useCallback(
        async (sepEventDetails: IShoppingWindowAllEvents, isEventPresent: boolean) => {
            const data: ISepEventDetails = {
                sepStartDate: sepEventDetails.sepStartDate,
                sepEndDate: sepEventDetails.sepEndDate,
                caseNumber: sepEventDetails.caseNumber,
                applicantEvents: getPlanWindowApplicantEvents(sepEventDetails, isEventPresent),
                overrideCommentText: sepEventDetails.overrideCommentText,
                coverageStartDate: sepEventDetails.coverageStartDate,
                isEventPresent: isEventPresent,
                isFinancial: sepEventDetails.isFinancial,
                applicationType: sepEventDetails.applicationType,
            };

            const showErrorModal = () => {
                closeModal('shoppingWindowModal');
                openModal('notifyModal', {
                    headerId: 'cap.applications.modal.openshoppingwindow2',
                    bodyContentId: 'cap.applications.modal.csrsubmissionfailtitle',
                    bodyMsgId: 'cap.applications.modal.srsubmissionfailcontent',
                    bodyMsgId2: 'cap.applications.modal.csrsubmissionfailcontent2',
                    buttonId: 'button.ok',
                });
            };

            try {
                const params = [
                    endpoints.MEMBER_APP_LIST_SAVE_SHOPPING_WINDOW.processReplacements({
                        encHouseHoldId: encHouseHoldId,
                    }),
                    data,
                    'POST',
                    true,
                    undefined,
                    'application/json;charset=utf-8',
                ];
                const response = await API.postData(params, dispatch, actions);
                if (response?.message?.toLowerCase() !== API_STATUS.SUCCESS) {
                    showErrorModal();
                } else {
                    dispatch(setApplicationProperty(sepEventDetails.caseNumber, 'endSepButtonEnabled', true));
                    closeModal('shoppingWindowModal');
                    openModal('notifyModal', {
                        headerId: 'cap.applications.csroption.openshoppingwindow.success',
                        bodyContentId: 'cap.applications.modal.applicationscsrsubmissionsucceed',
                        bodyMsgId: 'cap.applications.modal.csroktogoback',
                        buttonId: 'button.ok',
                    });
                }
            } catch (error) {
                showErrorModal();
            }
        },
        [],
    );

    return {
        fetchOpenShoppingWindowDetails: getOpenShoppingWindowDetails,
        saveShoppingWindowOverride,
    };
};
